import { makeStyles } from '@material-ui/core/styles';
import { claymorphismColors } from '../constants/colors';

export const useStyles = makeStyles((theme) => ({
  // Form container with warm background (matched with CTCI Banner)
  form: (props) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0 auto',
    padding: '24px',
    maxWidth: '1200px',
    background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 50%, #FFF3E0 100%)',
    minHeight: '100vh',
    [theme.breakpoints.down('sm')]: {
      padding: '16px',
      margin: '0 8px',
    },
  }),

  // Base Claymorphism Card (from skill: rounded 16-24px, thick borders 3-4px, inner+outer shadows)
  clayCard: {
    borderRadius: '20px',
    border: '3px solid rgba(255, 255, 255, 0.6)',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: `
      6px 6px 12px rgba(0, 0, 0, 0.08),
      -4px -4px 10px rgba(255, 255, 255, 0.8),
      inset 2px 2px 4px rgba(255, 255, 255, 0.6),
      inset -1px -1px 2px rgba(0, 0, 0, 0.03)
    `,
    transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `
        8px 8px 16px rgba(0, 0, 0, 0.1),
        -5px -5px 12px rgba(255, 255, 255, 0.9),
        inset 2px 2px 4px rgba(255, 255, 255, 0.6),
        inset -1px -1px 2px rgba(0, 0, 0, 0.03)
      `,
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '16px',
      boxShadow: `
        4px 4px 8px rgba(0, 0, 0, 0.06),
        -2px -2px 6px rgba(255, 255, 255, 0.7)
      `,
    },
  },

  // Color variants for cards (matched with CTCI Banner)
  clayCardOrange: { background: `linear-gradient(145deg, ${claymorphismColors.softOrange}, #FFE8CC)` },
  clayCardGold: { background: `linear-gradient(145deg, ${claymorphismColors.softGold}, #FFF8E1)` },
  clayCardCyan: { background: `linear-gradient(145deg, ${claymorphismColors.softCyan}, #B2EBF2)` },

  // Legacy color variants (kept for compatibility)
  clayCardPeach: { background: `linear-gradient(145deg, ${claymorphismColors.softPeach}, #FFD0C7)` },
  clayCardBlue: { background: `linear-gradient(145deg, ${claymorphismColors.babyBlue}, #9ECFE8)` },
  clayCardLilac: { background: `linear-gradient(145deg, ${claymorphismColors.lilac}, #D8D8F8)` },
  clayCardMint: { background: `linear-gradient(145deg, ${claymorphismColors.mint}, #7AE87A)` },
  clayCardLightPeach: { background: `linear-gradient(145deg, ${claymorphismColors.lightPeach}, #FFD8CC)` },
  clayCardLightBlue: { background: `linear-gradient(145deg, ${claymorphismColors.lightBlue}, #C0E8F5)` },
  clayCardLightMint: { background: `linear-gradient(145deg, ${claymorphismColors.lightMint}, #B5F0B0)` },

  // Hero section styling (matched with Banner orange)
  heroSection: {
    background: `linear-gradient(145deg, ${claymorphismColors.softOrange}, #FFE8CC)`,
    borderRadius: '24px',
    padding: '32px',
    marginBottom: '32px',
    border: '3px solid rgba(255, 255, 255, 0.5)',
    boxShadow: `
      8px 8px 16px rgba(255, 218, 179, 0.4),
      -4px -4px 12px rgba(255, 255, 255, 0.6),
      inset 2px 2px 4px rgba(255, 255, 255, 0.4)
    `,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '20px',
      borderRadius: '16px',
    },
  },
  heroTitle: {
    color: claymorphismColors.text,
    fontWeight: 700,
    marginBottom: '8px',
  },
  heroSubtitle: {
    color: claymorphismColors.text,
    opacity: 0.85,
  },

  // Section title with accent bar
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: claymorphismColors.text,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '""',
      width: '4px',
      height: '24px',
      borderRadius: '2px',
      marginRight: '12px',
      backgroundColor: claymorphismColors.accent,
    },
  },

  // Input field styling (soft, fluffy - skill keywords)
  clayInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.05)',
      border: '2px solid rgba(255, 255, 255, 0.5)',
      transition: 'all 0.2s ease-out',
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: '2px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: claymorphismColors.accent,
        boxShadow: `0 0 0 3px rgba(255, 112, 67, 0.2)`,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#607D8B',
      fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: claymorphismColors.accent,
    },
  },

  // Radio/Checkbox styling
  clayRadio: {
    '& .MuiRadio-root': {
      color: '#B0BEC5',
      '&.Mui-checked': {
        color: claymorphismColors.accent,
      },
    },
    '& .MuiCheckbox-root': {
      color: '#B0BEC5',
      '&.Mui-checked': {
        color: claymorphismColors.accent,
      },
    },
    '& .MuiFormControlLabel-label': {
      color: claymorphismColors.text,
      fontWeight: 400,
    },
  },

  // Submit button (soft press 200ms ease-out - skill spec)
  submitButton: {
    background: `linear-gradient(145deg, ${claymorphismColors.softPeach}, #FFB4A2)`,
    width: '14rem',
    height: '4.5rem',
    borderRadius: '20px',
    border: '3px solid rgba(255, 255, 255, 0.5)',
    boxShadow: `
      5px 5px 10px rgba(253, 188, 180, 0.4),
      -3px -3px 8px rgba(255, 255, 255, 0.5),
      inset 2px 2px 4px rgba(255, 255, 255, 0.4)
    `,
    transition: 'all 0.2s ease-out',
    '&:hover': {
      background: `linear-gradient(145deg, #FFB4A2, ${claymorphismColors.softPeach})`,
      transform: 'translateY(-2px)',
      boxShadow: `
        7px 7px 14px rgba(253, 188, 180, 0.5),
        -4px -4px 10px rgba(255, 255, 255, 0.6),
        inset 2px 2px 4px rgba(255, 255, 255, 0.4)
      `,
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: `
        2px 2px 5px rgba(253, 188, 180, 0.3),
        -1px -1px 4px rgba(255, 255, 255, 0.3),
        inset 3px 3px 6px rgba(0, 0, 0, 0.08)
      `,
    },
    '&:disabled': {
      background: 'linear-gradient(145deg, #CFD8DC, #B0BEC5)',
      boxShadow: `
        4px 4px 8px rgba(0, 0, 0, 0.1),
        -2px -2px 6px rgba(255, 255, 255, 0.5)
      `,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '280px',
    },
  },
  buttonLabel: {
    fontSize: '1.5rem',
    color: claymorphismColors.text,
    fontWeight: 600,
  },

  // Reminder box styling
  reminderBox: {
    background: `linear-gradient(145deg, ${claymorphismColors.lightPeach}, #FFE8DC)`,
    borderRadius: '20px',
    padding: '24px',
    marginTop: '24px',
    border: '3px solid rgba(255, 255, 255, 0.5)',
    boxShadow: `
      6px 6px 12px rgba(0, 0, 0, 0.08),
      -3px -3px 8px rgba(255, 255, 255, 0.8),
      inset 2px 2px 4px rgba(255, 255, 255, 0.5)
    `,
    textAlign: 'center',
  },
  reminderText: {
    color: claymorphismColors.accent,
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: '8px',
  },
  reminderSubText: {
    color: claymorphismColors.text,
    fontWeight: 500,
    marginBottom: '16px',
  },

  // Question title styling
  questionTitle: {
    color: claymorphismColors.text,
    fontWeight: 600,
  },
  questionTitleMissing: {
    color: claymorphismColors.errorRed,
    fontWeight: 700,
    backgroundColor: claymorphismColors.errorRedLight,
    padding: '4px 8px',
    borderRadius: '4px',
    borderLeft: `3px solid ${claymorphismColors.errorRed}`,
    display: 'inline-block',
  },
  requiredMark: {
    color: claymorphismColors.accent,
    marginRight: '4px',
    fontWeight: 700,
  },

  // Section Navigation Styles (Desktop/Tablet)
  sectionNav: {
    position: 'sticky',
    top: '20px',
    width: '140px',
    marginLeft: '16px',
    padding: '12px',
    borderRadius: '16px',
    background: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.08)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      width: '80px',
      padding: '8px',
    },
  },
  sectionNavTitle: {
    fontSize: '12px',
    fontWeight: 700,
    color: claymorphismColors.textLight,
    marginBottom: '12px',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  sectionNavItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 10px',
    marginBottom: '6px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-out',
    '&:hover': {
      backgroundColor: 'rgba(232, 93, 4, 0.1)',
      transform: 'translateX(2px)',
    },
    [theme.breakpoints.down('md')]: {
      padding: '8px',
      justifyContent: 'center',
    },
  },
  sectionNavItemActive: {
    backgroundColor: 'rgba(232, 93, 4, 0.15)',
  },
  sectionNavItemComplete: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
  },
  sectionNavItemError: {
    backgroundColor: claymorphismColors.errorRedLight,
  },
  sectionNavIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    marginRight: '8px',
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },
  sectionNavIconComplete: {
    backgroundColor: '#4CAF50',
    color: '#fff',
  },
  sectionNavIconError: {
    backgroundColor: claymorphismColors.errorRed,
    color: '#fff',
  },
  sectionNavIconPending: {
    backgroundColor: '#E0E0E0',
    color: '#757575',
  },
  sectionNavLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: claymorphismColors.text,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },

  // Mobile Drawer Navigation
  mobileNavDrawer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: '#fff',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
    transition: 'transform 0.3s ease-out',
  },
  mobileNavHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  },
  mobileNavHandleIcon: {
    width: '40px',
    height: '4px',
    backgroundColor: '#BDBDBD',
    borderRadius: '2px',
    position: 'absolute',
    left: '50%',
    top: '8px',
    transform: 'translateX(-50%)',
  },
  mobileNavProgress: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  mobileNavProgressText: {
    fontSize: '14px',
    fontWeight: 600,
    color: claymorphismColors.text,
  },
  mobileNavExpandText: {
    fontSize: '12px',
    color: claymorphismColors.accent,
    fontWeight: 600,
  },
  mobileNavContent: {
    maxHeight: '50vh',
    overflowY: 'auto',
    padding: '12px 16px 24px',
  },
  mobileNavItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    marginBottom: '8px',
    borderRadius: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    cursor: 'pointer',
    transition: 'all 0.2s ease-out',
    '&:hover': {
      backgroundColor: 'rgba(232, 93, 4, 0.08)',
    },
  },
  mobileNavItemComplete: {
    backgroundColor: 'rgba(76, 175, 80, 0.08)',
  },
  mobileNavItemError: {
    backgroundColor: claymorphismColors.errorRedLight,
  },
  mobileNavItemLabel: {
    fontSize: '15px',
    fontWeight: 500,
    color: claymorphismColors.text,
  },
  mobileNavItemStatus: {
    fontSize: '13px',
    fontWeight: 600,
  },
  mobileNavItemStatusComplete: {
    color: '#4CAF50',
  },
  mobileNavItemStatusError: {
    color: claymorphismColors.errorRed,
  },
  mobileNavOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 999,
  },

  // Form wrapper for navigation layout
  formWithNav: {
    display: 'flex',
    maxWidth: '1400px',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: '80px', // Space for mobile nav
    },
  },
  formContent: {
    flex: 1,
    maxWidth: '1200px',
  },

  // Navigation Submit Button Styles (Claymorphism)
  navSubmitButton: {
    width: '100%',
    marginTop: '16px',
    padding: '12px 16px',
    borderRadius: '12px',
    background: `linear-gradient(145deg, ${claymorphismColors.softPeach}, #FFB4A2)`,
    border: '2px solid rgba(255, 255, 255, 0.5)',
    boxShadow: `
      3px 3px 6px rgba(253, 188, 180, 0.3),
      -2px -2px 5px rgba(255, 255, 255, 0.4),
      inset 1px 1px 2px rgba(255, 255, 255, 0.3)
    `,
    transition: 'all 0.2s ease-out',
    cursor: 'pointer',
    '&:hover': {
      background: `linear-gradient(145deg, #FFB4A2, ${claymorphismColors.softPeach})`,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: `
        1px 1px 3px rgba(253, 188, 180, 0.2),
        inset 2px 2px 4px rgba(0, 0, 0, 0.05)
      `,
    },
    '&:disabled': {
      background: 'linear-gradient(145deg, #CFD8DC, #B0BEC5)',
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
  navSubmitButtonLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: claymorphismColors.text,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
    },
  },

  // Mobile nav collapsed submit button
  mobileNavCollapsedSubmit: {
    padding: '8px 16px',
    borderRadius: '10px',
    background: `linear-gradient(145deg, ${claymorphismColors.softPeach}, #FFB4A2)`,
    border: '2px solid rgba(255, 255, 255, 0.5)',
    boxShadow: `
      2px 2px 4px rgba(253, 188, 180, 0.3),
      -1px -1px 3px rgba(255, 255, 255, 0.4)
    `,
    cursor: 'pointer',
    transition: 'all 0.2s ease-out',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: 'linear-gradient(145deg, #CFD8DC, #B0BEC5)',
      cursor: 'not-allowed',
    },
  },
  mobileNavCollapsedSubmitLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: claymorphismColors.text,
    whiteSpace: 'nowrap',
  },

  // Mobile nav expanded submit button (at bottom of drawer)
  mobileNavExpandedSubmit: {
    width: '100%',
    marginTop: '12px',
    padding: '14px 20px',
    borderRadius: '12px',
    background: `linear-gradient(145deg, ${claymorphismColors.softPeach}, #FFB4A2)`,
    border: '2px solid rgba(255, 255, 255, 0.5)',
    boxShadow: `
      4px 4px 8px rgba(253, 188, 180, 0.3),
      -2px -2px 6px rgba(255, 255, 255, 0.5),
      inset 1px 1px 2px rgba(255, 255, 255, 0.3)
    `,
    cursor: 'pointer',
    transition: 'all 0.2s ease-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `
        5px 5px 10px rgba(253, 188, 180, 0.4),
        -3px -3px 8px rgba(255, 255, 255, 0.6)
      `,
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: `
        2px 2px 4px rgba(253, 188, 180, 0.2),
        inset 2px 2px 4px rgba(0, 0, 0, 0.05)
      `,
    },
    '&:disabled': {
      background: 'linear-gradient(145deg, #CFD8DC, #B0BEC5)',
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
  mobileNavExpandedSubmitLabel: {
    fontSize: '16px',
    fontWeight: 600,
    color: claymorphismColors.text,
    textAlign: 'center',
  },
}));

export default useStyles;
